import { recipeWriteVersion } from '../../../../services/recipeWriteConflict';
import { CostPriceAnalysis, CostPriceIngredient, CostPriceUpdateRequest, toIngredientUpdatePayload, updateCostPriceAnalysis } from '../../../../services/costPrice/costPriceService';
import { useCostPriceStore } from '../../../../stores/appCostPriceStore';

// The API regenerates every ingredient id on a whole-array write. Retain aliases
// for queued blur/click events and read the latest dish only once our turn begins.
const queues = new Map<string, Promise<unknown>>();
const aliases = new Map<string, Map<string, string>>();
export function serializeDishWrite<T>(dishId: string, operation: () => Promise<T>, requirePreviousSuccess = false): Promise<T> {
  const previous = queues.get(dishId) ?? Promise.resolve();
  const next = (requirePreviousSuccess ? previous : previous.catch(() => undefined)).then(operation);
  queues.set(dishId, next);
  void next.finally(() => { if (queues.get(dishId) === next) queues.delete(dishId); }).catch(() => undefined);
  return next;
}

export function resolveRowIndex(dish: CostPriceAnalysis, originalId: string): number {
  const currentIndex = dish.ingredients.findIndex(row => row.id === originalId);
  if (currentIndex >= 0) return currentIndex;
  const mapping = aliases.get(dish.id);
  let id = originalId;
  const seen = new Set<string>();
  while (mapping?.has(id) && !seen.has(id)) { seen.add(id); id = mapping.get(id)!; }
  return dish.ingredients.findIndex(row => row.id === id);
}

export async function writeDishIngredients(
  dishId: string,
  transform: (dish: CostPriceAnalysis) => Promise<CostPriceIngredient[]> | CostPriceIngredient[],
  extra: Pick<CostPriceUpdateRequest, 'markedIrrelevant'> = {},
) {
  const dish = useCostPriceStore.getState().byId(dishId);
  if (!dish) throw new Error('Dish no longer available');
  const rows = await transform(dish);
  const saved = await updateCostPriceAnalysis({ id: dishId, expectedUpdatedAt: recipeWriteVersion(dish), ingredients: rows.map(toIngredientUpdatePayload), ...extra });
  const mapping = aliases.get(dishId) ?? new Map<string, string>();
  rows.forEach((row, index) => { if (saved.ingredients[index] && row.id !== saved.ingredients[index].id) mapping.set(row.id, saved.ingredients[index].id); });
  aliases.set(dishId, mapping);
  useCostPriceStore.getState().upsertOne(saved);
  return saved;
}

export function stableRowKey(dishId: string, rowId: string): string {
  const mapping = aliases.get(dishId);
  let key = rowId;
  if (mapping) {
    const parents = new Map(Array.from(mapping).map(([from, to]) => [to, from]));
    while (parents.has(key)) key = parents.get(key)!;
  }
  return key;
}
